import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Container } from '../../components/layout/Container'
import { Card } from '../../components/ui/Card'
import { PlatformHero } from '../../components/sections/platform/PlatformHero'
import { ModuleOverview } from '../../components/sections/platform/ModuleOverview'
import { FeatureGrid } from '../../components/sections/platform/FeatureGrid'
import { MetricsSection } from '../../components/sections/platform/MetricsSection'
import { ModuleFAQ } from '../../components/sections/platform/ModuleFAQ'
import { PlatformCTA } from '../../components/sections/platform/PlatformCTA'
import { ModuleNav } from '../../components/sections/platform/ModuleNav'
import { useScrollAnimation } from '../../hooks/useScrollAnimation'

interface FlowExample {
  id: string
  title: string
  description: string
}

function FlowExamplesSection() {
  const { t } = useTranslation()
  const { ref, isInView } = useScrollAnimation()

  const items = t('platformPages.build.flows.items', { returnObjects: true }) as FlowExample[]

  return (
    <section ref={ref} className="border-y border-border py-24">
      <Container size="wide">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <span className="text-sm font-medium uppercase tracking-wider text-accent-light">
            {t('platformPages.build.flows.label')}
          </span>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-text-primary sm:text-4xl">
            {t('platformPages.build.flows.title')}
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Card variant="default" className="h-full">
                <div className="text-sm font-medium text-accent-light">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="mt-3 text-lg font-medium text-text-primary">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary">{item.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  )
}

export function BuildPage() {
  return (
    <>
      <PlatformHero tKey="build" />
      <ModuleOverview tKey="build" />
      <FeatureGrid tKey="build" />
      <FlowExamplesSection />
      <MetricsSection tKey="build" />
      <ModuleFAQ tKey="build" />
      <PlatformCTA />
      <ModuleNav />
    </>
  )
}
